import React, { useContext } from "react";
import { SocketContext } from "../../context/SocketContext";
import { AuthContext } from "../../context/AuthContext";
import classNames from "classnames";
import { Card, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Styles, MessageStyles } from "./Chat.styles";
import { Reactions } from "./Reactions";
import { MediaContext } from "../../context/MediaContext";
import { onDeleteMessage } from "../../utils/Alert";

const Chat = ({ replyClick }) => {
  const { chatMessages } = useContext(SocketContext);
  const { currentUser } = useContext(AuthContext);

  return (
    <Styles>
      <Col className="content">
        {Object.values(chatMessages).map((item, index) => {
          const parent = chatMessages[item.parentMsg];
          const isUser = item.sender.email === currentUser.email;

          return <Message key={index} message={item} isUser={isUser} parent={parent} replyClick={replyClick} />;
        })}
      </Col>
    </Styles>
  );
};

const Message = ({ message, isUser, parent, replyClick = null, markAsQuestion = null }) => {
  const { t } = useTranslation("", { keyPrefix: "components.chat" });

  const { currentUser } = useContext(AuthContext);
  const { setVideoTs, setSlideNumber, setIsFollowing } = useContext(MediaContext);
  const { sendSocketEvent } = useContext(SocketContext);

  const {
    uuid,
    text,
    isQuestion,
    deleted,
    updatedAt,
    videoTs,
    slideNumber,
    sender: { lastName, firstName },
  } = message;

  // date without seconds
  const when = new Date(updatedAt).toLocaleTimeString(t.i18n, {
    hour: "2-digit",
    minute: "2-digit",
  });

  const getReactionValues = () => {
    const values = new Array(5).fill(0).map(() => {
      return { value: 0, userReacted: false };
    });

    Object.values(message.reactions || {}).forEach((reaction) => {
      values[reaction.value - 5].value += 1;

      if (reaction.email === currentUser.email) {
        values[reaction.value - 5].userReacted = true;
      }
    });

    return values;
  };

  const deleteMsg = () => {
    onDeleteMessage({ uuid }, sendSocketEvent);
  };

  return (
    <MessageStyles isUser={isUser} isQuestion={isQuestion}>
      <Row className={classNames("align-items-center", { "justify-content-end": isUser })}>
        {!isUser && (
          <span className={classNames("w-auto message-title ms-2 fs-6 fw-bold", { "order-2": isUser })}>
            {`${lastName} ${firstName}`}
          </span>
        )}
      </Row>
      <Card
        className={classNames({
          "ms-4": isUser,
          "me-4": !isUser,
        })}
      >
        <Card.Body>
          {parent && (
            <div className="parent-card pt-2">
              <span className="m-0">
                <Card.Text className="parent-text p-0 m-0 fw-bold">{`${lastName} ${firstName}`}</Card.Text>
                <Card.Text className="parent-text p-0 m-0 text-truncate">{parent.text}</Card.Text>
              </span>
            </div>
          )}

          <div
            className={classNames({
              "pt-2": parent,
            })}
          >
            <Col className="d-flex flex-column p-1">
              {deleted ? <span className="fst-italic">{t("msg_deleted")}</span> : <span>{text}</span>}
              <Row>
                <span
                  className={classNames("w-auto text-nowrap message-time fs-6 mt-2", {
                    "me-auto": !isUser,
                    "order-2": isUser,
                    "ms-auto": isUser,
                  })}
                >
                  {replyClick && <i className="fas fa-reply reply-button" onClick={() => replyClick(message.uuid)} />}
                  {!deleted && <Reactions message={message} values={getReactionValues()} />}
                </span>
                <span
                  className={classNames("w-auto text-nowrap message-time fs-6 position-absolute bottom-0 pb-2", {
                    "end-0": !isUser,
                  })}
                >
                  {when}
                  {videoTs != null && (
                    <i className="fas fa-video ms-2 reference-buttons" onClick={() => setVideoTs(videoTs)} />
                  )}
                  {!deleted && <i className="fas fa-trash ms-2 reference-buttons" onClick={() => deleteMsg()} />}

                  {slideNumber !== null && slideNumber > 0 && (
                    <i
                      className="fas fa-file-pdf ms-2 reference-buttons"
                      onClick={() => {
                        setIsFollowing(false);
                        setSlideNumber(slideNumber);
                      }}
                    />
                  )}
                </span>
              </Row>
            </Col>
          </div>
        </Card.Body>
      </Card>
    </MessageStyles>
  );
};

export const MessageReply = ({ message }) => {
  const { t } = useTranslation("", { keyPrefix: "components.chat" });

  const {
    text,
    deleted,
    isQuestion,
    sender: { lastName, firstName },
  } = message;

  return (
    <>
      <Row className="align-items-center">
        <span className="w-auto message-title ms-2 fs-6 fw-bold ms-4">
          {t("replying_to")}
          <i className="fas fa-arrow-right fs-6 p-1" />
          {`${lastName} ${firstName}`}
        </span>
      </Row>
      <Card>
        <Card.Body className="p-2 m-0">
          <Card.Text>
            <Col className="d-flex flex-column p-1">
              {deleted ? (
                <span className="fst-italic">{isQuestion ? t("qst_deleted") : t("msg_deleted")}</span>
              ) : (
                <span>{text}</span>
              )}
            </Col>
          </Card.Text>
        </Card.Body>
      </Card>
    </>
  );
};

export default Chat;
